/**
 * Ganchos do motion para os blocos da Home (módulo leve, entra no pedaço de cada bloco). Num par fora da lista
 * (ativacao.ts) nenhum deles faz nada: não toca no DOM, não observa nada e não pede nenhum pedaço de JS. Num par da
 * lista, o motor (motor.ts), o GSAP e a cena do bloco chegam por import() depois que o bloco hidrata.
 *
 * Uso num bloco:
 *   const ativo = useHomeMotionAtivo();
 *   useMotionBloco(sectionRef, { ativo, cena: () => import('./motion').then((mod) => mod.cenaX), start: 'top 66%' });
 *   <section ref={sectionRef} data-motion-block={ativo ? 'x' : undefined}>…</section>
 */
import type { RefObject } from 'react';
import { useEffect, useSyncExternalStore } from 'react';

import type { MotionMode } from './modo';
import { entrarNoMotion, getMotionMode, isMotionMoving, onMotionModeChange } from './modo';
import type { MotionSceneBuilder, PresetBloco } from './motor';

export interface UseMotionBlocoOpcoes {
  /** O par da página está na lista do motion (useHomeMotionAtivo). */
  ativo: boolean;
  /** A cena própria do bloco, por import() (pedaço próprio). */
  cena?: () => Promise<MotionSceneBuilder>;
  /** Onde o topo do bloco precisa chegar para a chegada disparar (padrão 'top 82%'). */
  start?: string;
  preset?: PresetBloco;
}

const servidorOn = (): MotionMode => 'on';
const nadaParaAssinar = () => () => undefined;

/**
 * Modo atual, reativo (muda se o visitante ligar o movimento reduzido com a página aberta). Fora da lista não lê nem
 * assina nada: 'on' fixo (que fora da lista não liga nada).
 */
function useMotionMode(ativo: boolean): MotionMode {
  return useSyncExternalStore(ativo ? onMotionModeChange : nadaParaAssinar, ativo ? getMotionMode : servidorOn, servidorOn);
}

/**
 * Liga o motion de um bloco: a coreografia padrão + a cena própria, montadas depois da hidratação; desfaz ao desmontar
 * e ao entrar em Off (o bloco volta ao estado final na hora).
 */
export function useMotionBloco(ref: RefObject<HTMLElement | null>, { ativo, cena, start, preset = 'bloco' }: UseMotionBlocoOpcoes): void {
  const modo = useMotionMode(ativo);
  useEffect(() => {
    const el = ref.current;
    if (!ativo || !el) return undefined;
    const sair = entrarNoMotion();
    // o modo de agora (o valor do render da hidratação é o do servidor, 'on'): em Off nada é pedido
    if (getMotionMode() === 'off') return sair;
    let cancelado = false;
    let desmontar: (() => void) | null = null;
    Promise.all([import('./motor'), cena ? cena() : Promise.resolve(undefined)])
      .then(([motor, build]) => {
        if (cancelado) return;
        desmontar = motor.montarBloco(el, { preset, start, cena: build });
      })
      // sem o pedaço (rede caiu): o bloco fica como o HTML do servidor, que já é o estado final
      .catch(() => undefined);
    return () => {
      cancelado = true;
      desmontar?.();
      sair();
    };
    // a cena é montada uma vez por montagem do bloco (e de novo só se o modo mudar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo, modo]);
}

/**
 * O motion está em Off neste par (movimento reduzido, ambiente sem animações perpétuas ou `?motion=off`)? Fora da lista
 * e no servidor, false (o comportamento de antes). Os blocos usam para parar os laços próprios (giros, faixas).
 */
export function useMotionOff(ativo: boolean): boolean {
  const modo = useMotionMode(ativo);
  return ativo && modo === 'off';
}

/**
 * Marca `data-visivel` nos elementos enquanto estão perto da tela (margem de 10%). Os laços em CSS do kit (feixe) só
 * rodam com essa marca e com o modo On: fora da tela nada gasta CPU.
 */
export function useVisivel(refs: RefObject<Element | null>[], ativo: boolean, margem = '10% 0px'): void {
  const modo = useMotionMode(ativo);
  useEffect(() => {
    if (!ativo) return undefined;
    const sair = entrarNoMotion();
    if (getMotionMode() === 'off') return sair;
    const alvos = refs.map((ref) => ref.current).filter((el): el is Element => Boolean(el));
    if (!alvos.length || typeof IntersectionObserver === 'undefined') return sair;
    const observer = new IntersectionObserver((entradas) => entradas.forEach((entrada) => entrada.target.toggleAttribute('data-visivel', entrada.isIntersecting)), { rootMargin: margem });
    alvos.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
      alvos.forEach((el) => el.removeAttribute('data-visivel'));
      sair();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo, modo, margem]);
}

/**
 * Efeito avulso de motion fora da cena (ex.: a resposta do FAQ que abre): só num par da lista, com o modo On; o código
 * vem por import() e o modo é conferido de novo quando ele chega. `usar` pode devolver uma limpeza (ex.: a de
 * `efeito`, motor.ts). Devolve o cancelamento: use como retorno do efeito do React (troca, desmontagem). Falha de rede:
 * nada acontece (o estado final já está na tela).
 */
export function comMotion<M>(ativo: boolean, carregar: () => Promise<M>, usar: (mod: M) => void | (() => void)): () => void {
  if (!ativo || !isMotionMoving()) return () => undefined;
  let cancelado = false;
  let limpar: void | (() => void);
  carregar()
    .then((mod) => {
      if (cancelado || !isMotionMoving()) return;
      limpar = usar(mod);
    })
    .catch(() => undefined);
  return () => {
    cancelado = true;
    if (typeof limpar === 'function') limpar();
  };
}
